

import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button }from '@cmsgov/design-system';


// Components

import AccordionHeader from './AccordionHeader';
import CategoryHeader from './CategoryHeader';
import DocumentItem from './DocumentItem';
import EventInfo from './EventInfo';
import UploadHyperDashboard from '../hyperDashboard/hyperDashboards/UploadHyperDashboard';

// Hooks

import { useToggle } from '@hooks/useToggle';

// Utils

import {
    checkIfConfigFails,
    checkIfAllDocumentsProvided,
    getVerificationDocumentsConfig
} from '@utils/miscellaneous/getVerificationDocumentsConfig';
import { preventerTools } from '@utils/tools/preventerTools';

// Constants

import { checkUserStatus, USER_GROUP_ROLES } from '@constants/roles/userRoleTypes';
import { commonModalTypes } from '@constants/common/modals/commonModalTypes';
import { configs } from '@constants/configs/configs';
import { UPLOADED_DOCUMENT_STATUSES } from '@constants/common/miscellaneous';


const ApplicantDocumentInfo = props => {

    const {
        applicantInfo,
        applicantInfo: {
            applicantEventId,
            applicantGuid,
            documents,
            event,
            eventNotSupported,
            name
        },
        caseNumber,
        stateCode,
        isExtendedByDefault = true,
        isUploadHyperDashboardShown,
        parentActions: {
            overrideDocuments,
            removeDocument,
            showModal,
            submitDocuments,
            updateData,
            updateEnvironmentProp,
            updateMockData,
            uploadDocument,
            viewUploadedDocument
        },
        userRole
    } = props;

    const {
        INITIAL
    } = UPLOADED_DOCUMENT_STATUSES;

    const [ isExtended, toggleExtension ] = useToggle(isExtendedByDefault);

    const handleResponse = (response, applicantGuid = undefined) => {
        if(response.success) {
            const {
                payload,
                payload: {
                    applicants
                }
            } = response;

            if(applicantGuid) {
                const checkIfShouldResubmit = applicants
                    .find(a => a.applicantGuid === applicantGuid).shouldResubmit;

                checkIfShouldResubmit &&
                    showModal({
                        modalType: commonModalTypes.RESUBMIT_MODAL,
                        modalData: {
                            resubmitAction: handleSubmitClick
                        }
                    });
            }
            updateData(payload);
            return response;

        } else {
            showModal({
                modalType: commonModalTypes.REQUEST_ERROR,
                modalData: {
                    message: response.message
                }
            });
        }
    };

    const allDocumentsAreProvided = useMemo(
        () => checkIfAllDocumentsProvided(stateCode, event, documents),
        [ documents ]
    );

    const validationCompleted = useMemo(
        () => allDocumentsAreProvided &&
            documents.every(c =>
                c.documentTypes.every(dt => dt.documentStatus !== INITIAL)),
        [ allDocumentsAreProvided, documents ]
    );

    const isDisabled = useMemo(
        () => {
            switch(true) {
                case !allDocumentsAreProvided:
                    return true;
                default:
                    return false;
            }
        },
        [ allDocumentsAreProvided ]
    );

    const isSuperUser = useMemo(
        () => checkUserStatus(USER_GROUP_ROLES.SUPERUSERS, userRole),
        [ userRole ]
    );

    const handleSubmitClick = () => {
        if(configs.ENV.DEVELOPMENT) {
            updateMockData(
                'submit',
                [ applicantEventId ]
            );
        } else {
            submitDocuments({
                applicantEventId,
                caseNumber
            })
                .then(res => handleResponse(res, applicantGuid));
        }
    };


    const getDocuments = () => {

        return getVerificationDocumentsConfig(stateCode, event).documents
            .map((category, idx) => {

                const {
                    categoryType,
                    documentsQty,
                    documentTypes,
                    mandatoryDocuments = []
                } = category;

                const { documentTypes: receivedDocumentTypes, overrideDone } =
                    preventerTools.provideObject(
                        documents.find((c) => c.categoryType === categoryType),
                        { documentTypes: [], overrideDone: false } as any,
                    );

                return (
                    <div key = { categoryType } id = 'qle-category' className = 'qle-category'>
                        <CategoryHeader
                            { ...{
                                applicantEventId,
                                applicantInfo,
                                caseNumber,
                                categoryType,
                                documents,
                                documentsQty,
                                idx,
                                event,
                                mandatoryDocuments,
                                overrideDone,
                                parentActions: {
                                    handleResponse,
                                    overrideDocuments,
                                    showModal,
                                    updateMockData
                                },
                                receivedDocumentTypes,
                                stateCode,
                                userRole,
                                validationCompleted
                            } }
                        />
                        <ul className="qle-content-area">
                            <div className="qle-content-area--left"></div>
                            <div className="usa-list usa-list--unstyled qle-document-list qle-content-area--right">
                                {
                                    documentTypes.map((documentType, idx) => {
                                        return (
                                            <DocumentItem
                                                key = { `${documentType}_${idx}` }
                                                { ...{
                                                    applicantGuid,
                                                    applicantEventId,
                                                    caseNumber,
                                                    categoryType,
                                                    documentType,
                                                    documents,
                                                    event,
                                                    overrideDone,
                                                    parentActions: {
                                                        handleResponse,
                                                        removeDocument,
                                                        showModal,
                                                        updateMockData,
                                                        uploadDocument,
                                                        viewUploadedDocument
                                                    },
                                                    receivedDocumentTypes,
                                                    stateCode,
                                                    validationCompleted,
                                                    userRole
                                                } }
                                            />
                                        );
                                    })
                                }
                            </div>
                        </ul>
                    </div>
                );
            });
    };

    const configCheck = useMemo(
        () => {
            const configCheck = checkIfConfigFails(stateCode, event);
            return {
                valid: !configCheck,
                warning: configCheck
            };
        },
        [ stateCode, event ]
    );

    return (
        <div className = 'grid-row'>
            <div className = {`qle-individual-panel usa-accordion gi-accordion ${ isUploadHyperDashboardShown ? 'tablet:grid-col-10' : 'tablet:grid-col' }`}>
                <AccordionHeader
                    { ...{
                        applicantGuid,
                        isExtended,
                        name,
                        parentActions: {
                            toggleExtension
                        }
                    } }
                />
                {
                    isExtended &&

                    (
                        configCheck.valid ?
                            <div
                                aria-labelledby = { `accordion-trigger_${ applicantGuid }` }
                                className = 'gi-accordion-panel'
                                id = { `accordion-panel_${ applicantGuid }` }
                                role = 'region'
                            >
                                <EventInfo
                                    { ...{
                                        event,
                                        eventNotSupported,
                                        stateCode,
                                        validationCompleted,
                                        userRole
                                    } }
                                />
                                <div className = 'grid-row'>
                                    { getDocuments() }
                                </div>
                                {
                                    !validationCompleted &&

                                    <div className = 'grid-row'>
                                        <div className='desktop:grid-col-12'>
                                            <Button
                                                className = 'usa-button usa-button--primary gi-button gi-button__primary float-right'
                                                disabled = { isDisabled }
                                                onClick = { handleSubmitClick }
                                            >
                                                <FormattedMessage id = 'button.submit_documents'/>
                                            </Button>
                                        </div>
                                    </div>
                                }
                            </div> :
                            configCheck.warning
                    )
                }
            </div>

            {
                isUploadHyperDashboardShown &&

                <UploadHyperDashboard
                    { ...{
                        allDocumentsAreProvided,
                        applicantInfo,
                        isExtended,
                        isSuperUser,
                        parentActions: {
                            updateMockData,
                            updateEnvironmentProp
                        },
                        stateCode
                    } }
                />
            }

        </div>
    );
};

export default ApplicantDocumentInfo;